import { BUILDINGS, buildingFootprint, isBoxOrder } from '../../shared/constants'
import { tooSteep } from '../../shared/elevation'
import { DIRS, hexDist, hexKey, hexSpiral, type Axial } from '../../shared/hex'
import { moveCost } from '../../shared/terrain'
import type { Building } from '../../shared/types'
import { fogOf, fogVisible } from '../render/fog'
import { ghostValid } from '../render/renderer'
import { elevAt, ownerAt, roadAt, terrainAt, treeAt } from '../render/terrain'
import type { GameState } from '../state'

const SEARCH_RADIUS = 8
const MAX_WALK_COST = 10

/** Recommend a short supply trip from camp, not a distant tree-free clearing.
 * Only suggests placement; the ordinary preview/server still authorize a build. */
export function suggestOpeningPlot(
  st: GameState,
  hall: Pick<Building, 'q' | 'r'>,
  kind: 'farm' | 'house',
  visible: (plot: Axial) => boolean = () => true,
): Axial | null {
  const fog = fogOf(st)
  const blocked = new Set<string>()
  const ramps = new Set<string>()
  const edgeKey = (a: Axial, b: Axial): string => `${hexKey(a.q, a.r)}>${hexKey(b.q, b.r)}`
  for (const b of st.buildings.values()) {
    if (!b.done) continue
    if (b.type === 'ramp' && b.dir !== undefined) {
      const d = DIRS[((b.dir % 6) + 6) % 6]
      const end = { q: b.q + d.q, r: b.r + d.r }
      ramps.add(edgeKey(b, end)); ramps.add(edgeKey(end, b))
    }
    // Match the server's solid transit cores; hall annexes and farm rows are open.
    if (!BUILDINGS[b.type].blocks && (b.type === 'farm' || isBoxOrder(b.type) ||
      ['road', 'ramp', 'pier', 'dummy'].includes(b.type))) continue
    for (const p of buildingFootprint(b.type, b.q, b.r, b.dir)) {
      if (b.type === 'townhall' && (p.q !== b.q || p.r !== b.r)) continue
      blocked.add(hexKey(p.q, p.r))
    }
  }

  // One bounded Dijkstra search prices hills and mountain detours, respects
  // cliffs/ramps, and never invents a route through water or unseen terrain.
  const costs = new Map<string, number>([[hexKey(hall.q, hall.r), 0]])
  const frontier = [{ q: hall.q, r: hall.r, cost: 0 }]
  while (frontier.length) {
    frontier.sort((a, b) => b.cost - a.cost)
    const from = frontier.pop()!
    if (from.cost !== costs.get(hexKey(from.q, from.r))) continue
    for (const d of DIRS) {
      const to = { q: from.q + d.q, r: from.r + d.r }
      if (hexDist(hall.q, hall.r, to.q, to.r) > SEARCH_RADIUS) continue
      const key = hexKey(to.q, to.r)
      if (blocked.has(key) || !fogVisible(fog, to.q, to.r)) continue
      const terrain = terrainAt(st, to.q, to.r)
      const owner = ownerAt(st, to.q, to.r)
      if (terrain === undefined || (owner !== undefined && owner !== st.me)) continue
      if (tooSteep(elevAt(st, from.q, from.r) ?? 0, elevAt(st, to.q, to.r) ?? 0) &&
        !ramps.has(edgeKey(from, to))) continue
      const cost = from.cost + moveCost(terrain, roadAt(st, to.q, to.r))
      if (cost > MAX_WALK_COST || cost >= (costs.get(key) ?? Infinity)) continue
      costs.set(key, cost)
      frontier.push({ ...to, cost })
    }
  }

  const plots: (Axial & { cost: number; distance: number; trees: number })[] = []
  for (const p of hexSpiral(hall.q, hall.r, SEARCH_RADIUS)) {
    const cells = buildingFootprint(kind, p.q, p.r, kind === 'farm' ? st.pierRotate : undefined)
    const cost = Math.max(...cells.map(c => costs.get(hexKey(c.q, c.r)) ?? Infinity))
    if (!Number.isFinite(cost) || !ghostValid(st, kind, p.q, p.r)) continue
    plots.push({ ...p, cost,
      distance: Math.max(...cells.map(c => hexDist(hall.q, hall.r, c.q, c.r))),
      trees: cells.filter(c => treeAt(st, c.q, c.r)).length,
    })
  }
  plots.sort((a, b) => a.cost - b.cost || a.distance - b.distance || a.trees - b.trees)
  const best = plots[0]
  if (!best) return null
  // A panel/camera can hide the best site, but must not send the player far away.
  const shown = plots.find(p => p.cost <= best.cost + 1 && p.distance <= best.distance + 1 && visible(p))
  return shown ? { q: shown.q, r: shown.r } : null
}
